/**
 * @fileoverview `ColorSwatch` — tuile d'une couleur du design system.
 *
 * Affiche un rectangle de la couleur, son nom lisible, la classe Tailwind
 * correspondante (ex : `bg-quipux-navy-500`) et la valeur hex extraite via
 * `getComputedStyle`. Le hex vient du DOM à chaud pour rester synchrone
 * avec `globals.css` : si un token change, tous les swatches se mettent
 * à jour au reload sans qu'on ait à répéter les valeurs ici.
 *
 * Le hex ET la classe Tailwind sont copiables via `<CopyButton>` — utile
 * pour un développeur qui veut réutiliser un token dans son composant.
 */

import { useEffect, useRef, useState } from 'react';
import { cn, CopyButton } from '@gnpc/ui';

interface ColorSwatchProps {
  /** Nom lisible affiché en dessous du swatch (ex : `navy-500`). */
  name: string;
  /**
   * Classe utilitaire Tailwind qui applique la couleur en background.
   * Ex : `bg-quipux-navy-500`, `bg-primary`, `bg-status-paid`.
   */
  className: string;
  /**
   * Force le texte en clair — utile pour les couleurs sombres où le
   * hex affiché en survol doit rester lisible.
   */
  dark?: boolean;
}

/**
 * Tuile de couleur. Rendu à hauteur fixe pour aligner les grilles.
 */
export function ColorSwatch({ name, className, dark = false }: ColorSwatchProps): JSX.Element {
  const ref = useRef<HTMLDivElement | null>(null);
  const [hex, setHex] = useState<string>('');

  useEffect(() => {
    if (!ref.current) return;
    // Lecture DOM à chaud : `background-color` en `rgb(r, g, b)`, converti en hex.
    const rgb = window.getComputedStyle(ref.current).backgroundColor;
    setHex(rgbToHex(rgb));
  }, [className]);

  return (
    <div className="flex flex-col gap-1.5">
      <div
        ref={ref}
        className={cn(
          'flex h-16 items-end justify-end rounded-md border border-border/70 p-2 font-mono text-[10px]',
          className,
          dark ? 'text-white/70' : 'text-quipux-navy-950/60',
        )}
      >
        {hex}
      </div>
      <div className="flex items-start justify-between gap-1">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-medium text-quipux-navy-900">{name}</p>
          <code className="block truncate font-mono text-[10px] text-muted-foreground">
            {className}
          </code>
        </div>
        <div className="flex shrink-0 flex-col">
          {hex && (
            <CopyButton
              value={hex}
              ariaLabel={`Copier le code hexadécimal ${hex} de ${name}`}
            />
          )}
          <CopyButton
            value={className}
            ariaLabel={`Copier la classe Tailwind ${className}`}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Convertit un `rgb(r, g, b)` ou `rgba(r, g, b, a)` en `#RRGGBB`.
 * Retourne la chaîne d'entrée si le format n'est pas reconnu — évite
 * de casser l'affichage sur une valeur inattendue.
 */
function rgbToHex(rgb: string): string {
  const match = rgb.match(/\d+/g);
  if (!match || match.length < 3) return rgb;
  const [r, g, b] = match.map((n) => Number.parseInt(n, 10));
  const toHex = (n: number): string => n.toString(16).padStart(2, '0');
  return `#${toHex(r ?? 0)}${toHex(g ?? 0)}${toHex(b ?? 0)}`.toUpperCase();
}
